import Store from '@ember-data/store';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import currency from 'currency.js';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import dateUtils from 'donor-elf-web/lib/date-utils';
import ContactGiftSummaryModel from 'donor-elf-web/models/contact-gift-summary';
import FormatDateService from 'donor-elf-web/services/format-date-service';
import FundGoalService from 'donor-elf-web/services/fund-goal-service';
import moment from 'moment';

class BothYearsRecord {
  constructor(
    public currentYearRecord: ContactGiftSummaryModel,
    public previousYearRecord: ContactGiftSummaryModel,
  ) {
    this.contactId = currentYearRecord.contactId;
    this.giftCountDiff =
      currentYearRecord.giftCount - previousYearRecord.giftCount;
    this.giftSumDiff = currency(currentYearRecord.giftSum).subtract(
      previousYearRecord.giftSum,
    ).value;
  }

  contactId!: string;
  giftCountDiff!: number;
  giftSumDiff!: number;
}

export default class ComparisonController extends Controller {
  @service declare formatDateService: FormatDateService;
  @service declare fundGoalService: FundGoalService;
  @service declare store: Store;

  @tracked currentYearStartDate = '2023-01-01'; // placeholder until it's set on templateDidInsert()
  @tracked loading = false;

  @tracked currentYearRecords?: ContactGiftSummaryModel[];
  @tracked previousYearRecords?: ContactGiftSummaryModel[];

  @tracked currentYearContactIds?: Set<string>;
  @tracked previousYearContactIds?: Set<string>;

  @tracked newDonorRecords?: ContactGiftSummaryModel[];
  @tracked returningDonorRecords?: ContactGiftSummaryModel[];
  @tracked lapsedDonorRecords?: ContactGiftSummaryModel[];
  @tracked lapsedDonorsThatReturnedRecords?: ContactGiftSummaryModel[];

  @tracked bothYearsRecords?: BothYearsRecord[];
  @tracked increasedDonorRecords?: BothYearsRecord[];
  @tracked sameAmountDonorRecords?: BothYearsRecord[];
  @tracked decreasedDonorRecords?: BothYearsRecord[];

  get defaultCurrentYearStartDate() {
    if (this.fundGoalService.hasYtdGoalType) {
      return this.fundGoalService.ytdStartMoment
        .clone()
        .subtract(1, 'years')
        .format('YYYY-MM-DD');
    } else {
      return new moment()
        .subtract(1, 'years')
        .startOf('year')
        .format('YYYY-MM-DD');
    }
  }

  get currentYearEndDate() {
    return new moment(this.currentYearStartDate)
      .add(1, 'year')
      .subtract(1, 'day')
      .format('YYYY-MM-DD');
  }

  get previousYearStartDate() {
    return new moment(this.currentYearStartDate)
      .subtract(1, 'year')
      .format('YYYY-MM-DD');
  }

  get previousYearEndDate() {
    return new moment(this.currentYearStartDate)
      .subtract(1, 'day')
      .format('YYYY-MM-DD');
  }

  get currentYearLabel() {
    if (this.currentYearStartDateIsBeginningOfYear) {
      return `current year (${this.currentYearStartDate.split('-')[0]})`;
    } else {
      return `current year (${this.formatDateService.format(this.currentYearStartDate)}-${this.formatDateService.format(this.currentYearEndDate)})`;
    }
  }

  get previousYearLabel() {
    if (this.currentYearStartDateIsBeginningOfYear) {
      return `previous year (${this.previousYearStartDate.split('-')[0]})`;
    } else {
      return `previous year (${this.formatDateService.format(this.previousYearStartDate)}-${this.formatDateService.format(this.previousYearEndDate)})`;
    }
  }

  get currentYearStartDateIsBeginningOfYear() {
    return this.currentYearStartDate.endsWith('-01-01');
  }

  get currentYearTotalSum() {
    return this.currentYearRecords
      ?.map((x) => x.giftSum)
      .reduce((prevVal, amount) => {
        return currency(prevVal).add(amount).value;
      }, 0);
  }

  get previousYearTotalSum() {
    return this.previousYearRecords
      ?.map((x) => x.giftSum)
      .reduce((prevVal, amount) => {
        return currency(prevVal).add(amount).value;
      }, 0);
  }

  get currentYearDonorCountMinusPreviousYearDonorCount() {
    return (
      (this.currentYearRecords?.length || 0) -
      (this.previousYearRecords?.length || 0)
    );
  }

  get currentYearTotalSumMinusPreviousYearTotalSum() {
    return currency(this.currentYearTotalSum).subtract(
      this.previousYearTotalSum,
    ).value;
  }

  get newDonorTotalSum() {
    return this.newDonorRecords
      ?.map((x) => x.giftSum)
      .reduce((prevVal, amount) => {
        return currency(prevVal).add(amount).value;
      }, 0);
  }

  get lapsedDonorTotalSum() {
    return (
      this.lapsedDonorRecords
        ?.map((x) => x.giftSum)
        .reduce((prevVal, amount) => {
          return currency(prevVal).add(amount).value;
        }, 0) * -1
    );
  }

  get lapsedDonorsThatReturnedTotalSum() {
    return (
      this.lapsedDonorsThatReturnedRecords
        ?.map((x) => x.giftSum)
        .reduce((prevVal, amount) => {
          return currency(prevVal).add(amount).value;
        }, 0) * -1
    );
  }

  get returningDonorTotalSum() {
    return this.returningDonorRecords
      ?.map((x) => x.giftSum)
      .reduce((prevVal, amount) => {
        return currency(prevVal).add(amount).value;
      }, 0);
  }

  get increasedDonorTotalSum() {
    return this.increasedDonorRecords
      ?.map((x) => x.giftSumDiff)
      .reduce((prevVal, amount) => {
        return currency(prevVal).add(amount).value;
      }, 0);
  }

  get sameAmountDonorTotalSum() {
    return this.sameAmountDonorRecords
      ?.map((x) => x.currentYearRecord.giftSum)
      .reduce((prevVal, amount) => {
        return currency(prevVal).add(amount).value;
      }, 0);
  }

  get decreasedDonorTotalSum() {
    return this.decreasedDonorRecords
      ?.map((x) => x.giftSumDiff)
      .reduce((prevVal, amount) => {
        return currency(prevVal).add(amount).value;
      }, 0);
  }

  @action
  templateDidInsert() {
    this.currentYearStartDate = this.defaultCurrentYearStartDate;
    this.load();
  }

  @action
  templateWillDestroy() {
    this.loading = false;
  }

  async load() {
    this.loading = true;
    let currentYearStartDateObject = dateUtils.deserializeDateOnly(
      this.currentYearStartDate,
    )!;
    let currentYearEndDateObject = dateUtils.deserializeDateOnly(
      this.currentYearEndDate,
    )!;

    this.currentYearRecords = sortByProperties(
      (
        await this.store.query('contact-gift-summary', {
          filter: {
            from: this.currentYearStartDate,
            to: this.currentYearEndDate,
          },
        })
      ).slice(),
      ['giftSum:desc'],
    );
    this.previousYearRecords = sortByProperties(
      (
        await this.store.query('contact-gift-summary', {
          filter: {
            from: this.previousYearStartDate,
            to: this.previousYearEndDate,
          },
        })
      ).slice(),
      ['giftSum:desc'],
    );

    this.currentYearContactIds = new Set(
      this.currentYearRecords?.map((x) => x.contactId) || [],
    );
    this.previousYearContactIds = new Set(
      this.previousYearRecords?.map((x) => x.contactId) || [],
    );

    let newDonorContactIds = this.currentYearContactIds.difference(
      this.previousYearContactIds,
    );
    this.newDonorRecords = this.currentYearRecords.filter(
      (x) =>
        newDonorContactIds.has(x.contactId) &&
        x.firstGiftDate >= currentYearStartDateObject,
    );
    this.returningDonorRecords = this.currentYearRecords.filter(
      (x) =>
        newDonorContactIds.has(x.contactId) &&
        x.firstGiftDate < currentYearStartDateObject,
    );

    let lapsedDonorContactIds = this.previousYearContactIds.difference(
      this.currentYearContactIds,
    );
    this.lapsedDonorRecords = this.previousYearRecords.filter(
      (x) =>
        lapsedDonorContactIds.has(x.contactId) &&
        x.lastGiftDate <= currentYearEndDateObject,
    );
    this.lapsedDonorsThatReturnedRecords = this.previousYearRecords.filter(
      (x) =>
        lapsedDonorContactIds.has(x.contactId) &&
        x.lastGiftDate > currentYearEndDateObject,
    );

    let bothYearsContactIds = this.currentYearContactIds.intersection(
      this.previousYearContactIds,
    ) as string[];
    let bothYearRecords: BothYearsRecord[] = [];
    for (let contactId of bothYearsContactIds) {
      bothYearRecords.push(
        new BothYearsRecord(
          this.currentYearRecords!.find((x) => x.contactId === contactId)!,
          this.previousYearRecords!.find((x) => x.contactId === contactId)!,
        ),
      );
    }
    this.bothYearsRecords = sortByProperties(bothYearRecords, [
      'giftSumDiff:desc',
    ]);
    this.increasedDonorRecords = this.bothYearsRecords.filter(
      (x) => x.giftSumDiff > 0,
    );
    this.sameAmountDonorRecords = sortByProperties(
      this.bothYearsRecords.filter((x) => x.giftSumDiff === 0),
      ['currentYearRecord.giftSum:desc'],
    );
    this.decreasedDonorRecords = sortByProperties(
      this.bothYearsRecords.filter((x) => x.giftSumDiff < 0),
      ['giftSumDiff:asc'],
    );

    this.loading = false;
  }
}
